import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  BookOpen,
  Code2,
  FlaskConical,
  History,
  MessagesSquare,
  Palette,
  Plus,
  Sparkles,
} from 'lucide-react';
import { getBotProfile, listInboxes, putBotProfile } from '../../api/endpoints';
import { ApiError } from '../../api/envelope';
import { getSiteIntegrations } from '../../api/siteIntegrations';
import type { BotProfile, Inbox } from '../../api/types';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { RailItem, type RailEntry } from '../../components/setup/SetupKit';
import { Spinner } from '../../components/Spinner';
import { useCapabilities } from '../../lib/capabilities';
import { isManagedInbox } from '../../lib/channelMeta';
import { useLoad } from '../../lib/useLoad';
import { RunsCard } from './BotActivity';
import { ChatWidgetSection } from './BotAppearance';
import { AssistantSection } from './BotAssistant';
import { ChannelsSection } from './BotChannels';
import { CreateChatForm, InstallSection, type WebsiteInbox } from './BotInstall';
import { BotKnowledge } from './BotKnowledge';
import { BotPlayground } from './BotPlayground';
import { BotSetupWizard } from './BotSetupWizard';
import { type ChatLink, type ChatSection, chatLinkOf, chatLive } from './botSetup';
import '../../components/setup/setup.css';
import './bot.css';

/**
 * Everything about the chatbot in one place, as a rail of named sections — the
 * analytics install screen's shape, on purpose, so the two website settings
 * screens read the same way.
 *
 * It used to be six tabs over one column, and putting the assistant on a site
 * was the confusing part: the website chat was created on the Connect screen,
 * the assistant was pointed at it on a tab called Channels & install whose
 * snippet only appeared after a save, the sites allowed to show it lived under
 * the widget's look, and nothing said whether any of it had worked. Install is
 * now the first section and does all of that in order, beside whether the chat
 * is live and what is left to do.
 *
 * Every section stays mounted once opened and is only hidden, so an edit
 * survives a look at another section — the rail marks which ones hold one.
 * They mount on first VISIT rather than with the screen, because the
 * Playground starts a real visitor session the moment it renders.
 *
 * The same component is framed by the dispatch dashboard (My website →
 * Chatbot, `/website-embed`), where there is no router to leave by: anything
 * that must leave the screen goes through `useConsoleNav`, and the section is
 * deliberately NOT in the address — `?section=` is how a link opens it, not
 * where the reader is.
 */

const SETUP: RailEntry<ChatSection>[] = [
  { key: 'install', label: 'Install', hint: 'Put it on your website', icon: Code2 },
  { key: 'assistant', label: 'Assistant', hint: 'Name, instructions, rules', icon: Sparkles },
  { key: 'knowledge', label: 'What it knows', hint: 'The answers it can give', icon: BookOpen },
  { key: 'widget', label: 'Chat widget', hint: 'How it looks on your site', icon: Palette },
  { key: 'channels', label: 'Channels', hint: 'Where else it answers', icon: MessagesSquare },
];

const CHECK: RailEntry<ChatSection>[] = [
  { key: 'playground', label: 'Playground', hint: 'Try it before customers do', icon: FlaskConical },
  { key: 'activity', label: 'Activity', hint: 'Every run, and why', icon: History },
];

const ALL = [...SETUP, ...CHECK];

const isWebsite = (inbox: Inbox): inbox is WebsiteInbox =>
  inbox.channelType === 'website' && isManagedInbox(inbox);

export function BotScreen({
  framed = false,
  link = null,
  onOpenConversations = null,
}: {
  framed?: boolean;
  link?: ChatLink | null;
  /**
   * Where the assistant's conversations are read — this screen is its
   * settings. Framed, only the host can open Communications, and only once it
   * has said so; standalone it is the console's own.
   */
  onOpenConversations?: (() => void) | null;
}) {
  const { search } = useLocation();
  const navigate = useNavigate();
  const toConversations = onOpenConversations ?? (framed ? null : () => navigate('/comms'));
  // Standalone, a link arrives on the address (`/bot?section=install&inbox=12`);
  // framed, the embed reads its own address and hands it over.
  const opening = useMemo(() => (framed ? link : chatLinkOf(search)), [framed, link, search]);
  const profile = useLoad(getBotProfile);
  const refused = profile.error instanceof ApiError && profile.error.kind === 'forbidden';

  const body = () => {
    if (profile.loading) {
      return (
        <div className="home-loading">
          <Spinner large />
        </div>
      );
    }
    if (refused) {
      return (
        <Note tone="info">
          Setting up the chatbot needs the <b>Chatbot</b> permission. An administrator grants it under Settings →
          Users &amp; access.
        </Note>
      );
    }
    if (profile.error) return <ErrorNote error={profile.error} />;
    if (profile.data === null) {
      return <BotSetupWizard onCreated={(fresh) => profile.setData(fresh)} />;
    }
    return (
      <BotManage
        profile={profile.data}
        // A brand-new assistant lands on Install, which is what the wizard said comes next.
        opening={opening}
        onProfile={(fresh) => profile.setData(fresh)}
        onOpenConversations={toConversations}
      />
    );
  };

  return (
    <main className={framed ? 'bot-page setup-tokens is-framed' : 'page bot-page setup-tokens'}>
      {profile.data ? null : (
        <div className="page-head">
          <div>
            <h1 className="page-title">Chatbot</h1>
            <p className="page-sub">An AI assistant that answers conversations before your team gets there.</p>
          </div>
        </div>
      )}
      {body()}
    </main>
  );
}

function BotManage({
  profile,
  opening,
  onProfile,
  onOpenConversations,
}: {
  profile: BotProfile;
  opening: ChatLink | null;
  onProfile: (fresh: BotProfile) => void;
  onOpenConversations: (() => void) | null;
}) {
  const { me } = useCapabilities();
  // Permissive while /meta is in flight, as everywhere: the server refuses
  // what the reader may not do, and a slow lookup must not hide the form.
  const mayCreate = me?.access.features.connectChannels ?? true;
  const inboxes = useLoad(listInboxes);
  const integrations = useLoad(getSiteIntegrations);
  // A channel saved on this screen, ahead of the list's own re-read.
  const [overrides, setOverrides] = useState<Record<number, Inbox>>({});
  const rows = useMemo(
    () => (inboxes.data ?? []).map((inbox) => overrides[inbox.id] ?? inbox),
    [inboxes.data, overrides],
  );
  const websites = useMemo(() => rows.filter(isWebsite), [rows]);
  const theirs = rows.some((inbox) => inbox.channelType === 'website') && !websites.length;

  const [chatId, setChatId] = useState<number | null>(opening?.inboxId ?? null);
  // A chat the assistant answers first, then the oldest: the one an operator
  // coming back to this screen is most likely here about.
  const chat =
    websites.find((inbox) => inbox.id === chatId) ??
    websites.find((inbox) => profile.inboxIds.includes(inbox.id)) ??
    websites[0] ??
    null;

  const [section, setSection] = useState<ChatSection>(opening?.section ?? 'install');
  const [visited, setVisited] = useState<Set<ChatSection>>(() => new Set([opening?.section ?? 'install']));
  const [dirty, setDirty] = useState<Partial<Record<ChatSection, boolean>>>({});
  // What is waiting on "discard your edits?": another chat, or leaving the screen.
  const [pending, setPending] = useState<{ run: () => void; leaving: boolean } | null>(null);
  const [adding, setAdding] = useState(false);
  const paneRef = useRef<HTMLDivElement>(null);

  const onDirty = useCallback(
    (key: ChatSection, value: boolean) =>
      setDirty((was) => (Boolean(was[key]) === value ? was : { ...was, [key]: value })),
    [],
  );

  const go = useCallback((key: ChatSection) => {
    setSection(key);
    setVisited((was) => (was.has(key) ? was : new Set(was).add(key)));
    // The rail is pinned and a section can be long: chosen from the foot of
    // another, the new one would otherwise open halfway down.
    const pane = paneRef.current;
    if (pane && pane.getBoundingClientRect().top < 0) pane.scrollIntoView({ block: 'start', inline: 'nearest' });
  }, []);

  const onInbox = useCallback((fresh: Inbox) => setOverrides((all) => ({ ...all, [fresh.id]: fresh })), []);
  const onCreated = (fresh: Inbox) => {
    onInbox(fresh);
    setChatId(fresh.id);
    setAdding(false);
    inboxes.reload();
    go('install');
  };

  /** Switching chat swaps what Chat widget is editing, so its edits would go with it. */
  const pickChat = (id: number) => {
    const action = () => setChatId(id);
    if (dirty.widget) setPending({ run: action, leaving: false });
    else action();
  };

  const live = chat ? chatLive(chat.channel) : null;
  const installStatus =
    live === 'live'
      ? ({ tone: 'ok', label: 'Live on your site' } as const)
      : live
        ? ({ tone: 'warn', label: live === 'quiet' ? 'Quiet 24h' : 'Not on a site yet' } as const)
        : null;

  const pane = (key: ChatSection, render: () => ReactNode) =>
    visited.has(key) ? (
      <div key={key} className="an-manage-section" data-pane={key} hidden={section !== key}>
        {render()}
      </div>
    ) : null;

  const unsaved = ALL.filter((entry) => dirty[entry.key]);

  return (
    <div className="an-manage bot-manage">
      <div className="an-manage-head">
        <div className="bot-manage-id">
          <h1 className="page-title">Chatbot</h1>
          <p className="page-sub">
            {profile.name} answers conversations before your team gets there.
            {onOpenConversations ? (
              <>
                {' '}
                <button
                  type="button"
                  className="an-link"
                  onClick={() => {
                    if (unsaved.length) setPending({ run: onOpenConversations, leaving: true });
                    else onOpenConversations();
                  }}
                >
                  Read them in Communications
                </button>
              </>
            ) : null}
          </p>
        </div>
        <div className="bot-manage-controls">
          {websites.length > 1 && chat ? (
            <select
              className="select"
              aria-label="Website chat"
              value={chat.id}
              onChange={(event) => pickChat(Number(event.target.value))}
            >
              {websites.map((inbox) => (
                <option key={inbox.id} value={inbox.id}>
                  {inbox.name}
                </option>
              ))}
            </select>
          ) : null}
          {mayCreate && websites.length ? (
            <button type="button" className="btn btn-secondary" onClick={() => setAdding(true)}>
              <Plus size={14} /> Add website chat
            </button>
          ) : null}
          <AssistantSwitch profile={profile} onProfile={onProfile} />
        </div>
      </div>

      {inboxes.error ? <ErrorNote error={inboxes.error} /> : null}

      <div className="an-manage-split">
        <nav className="an-manage-nav" aria-label="Chatbot settings">
          <span className="an-manage-navgroup">Set up</span>
          {SETUP.map((entry) => (
            <RailItem
              key={entry.key}
              entry={entry}
              on={section === entry.key}
              dirty={Boolean(dirty[entry.key])}
              status={entry.key === 'install' ? installStatus : null}
              onGo={go}
            />
          ))}
          <span className="an-manage-navgroup">Check it</span>
          {CHECK.map((entry) => (
            <RailItem key={entry.key} entry={entry} on={section === entry.key} onGo={go} />
          ))}
        </nav>

        <div className="an-manage-pane" ref={paneRef}>
          {inboxes.loading && !inboxes.data ? (
            <Spinner />
          ) : (
            <>
              {pane('install', () => (
                <InstallSection
                  profile={profile}
                  inbox={chat}
                  theirs={theirs}
                  mayCreate={mayCreate}
                  integrations={integrations.data}
                  refreshing={inboxes.refreshing}
                  onCheck={() => {
                    // A fresh read of the channel IS the check: the stamp is on it.
                    setOverrides({});
                    inboxes.reload();
                  }}
                  onProfile={onProfile}
                  onInbox={onInbox}
                  onCreated={onCreated}
                  onGo={go}
                />
              ))}
              {pane('assistant', () => (
                <AssistantSection profile={profile} onSaved={onProfile} onDirty={onDirty} />
              ))}
              {pane('knowledge', () => (
                <div className="bot-narrow">
                  <BotKnowledge />
                </div>
              ))}
              {pane('widget', () => (
                <ChatWidgetSection inbox={chat} theirs={theirs} onSaved={onInbox} onDirty={onDirty} onGo={go} />
              ))}
              {pane('channels', () => (
                <ChannelsSection
                  // Re-read whenever another section moves the assignment, so
                  // the list never offers to save back an older one.
                  key={profile.inboxIds.join(',')}
                  profile={profile}
                  inboxes={rows}
                  onProfile={onProfile}
                  onGo={go}
                />
              ))}
              {pane('playground', () => <BotPlayground enabled={profile.enabled} />)}
              {pane('activity', () => <RunsCard />)}
            </>
          )}
        </div>
      </div>

      {adding ? (
        <AddChatDialog
          profile={profile}
          onProfile={onProfile}
          onCreated={onCreated}
          onCancel={() => setAdding(false)}
        />
      ) : null}

      {pending ? (
        <ConfirmDialog
          title="Discard unsaved changes?"
          body={
            <p>
              {unsaved.map((entry) => entry.label).join(' and ')}{' '}
              {unsaved.length === 1 ? 'has changes' : 'have changes'} that are not saved.{' '}
              {pending.leaving ? 'Leaving' : 'Switching chat'} now throws them away.
            </p>
          }
          confirmLabel="Discard changes"
          danger
          onConfirm={() => {
            const { run, leaving } = pending;
            setPending(null);
            if (!leaving) onDirty('widget', false);
            run();
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * On or off, in the header: the one decision an operator comes back to most,
 * saved the moment it is flipped. It used to be a toggle inside the Assistant
 * form, where turning it on also saved whatever else was half-edited there —
 * and a new assistant is created OFF, so this is the second act of every setup.
 */
function AssistantSwitch({ profile, onProfile }: { profile: BotProfile; onProfile: (fresh: BotProfile) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const draft = profile.flags.replyMode === 'draft';

  const flip = async (enabled: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const fresh = await putBotProfile({ enabled });
      onProfile({ ...fresh, inboxIds: fresh.inboxIds ?? profile.inboxIds });
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'Could not switch the assistant.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bot-switch" title={error ?? undefined}>
      <span className={profile.enabled ? 'bot-switch-state is-on' : 'bot-switch-state'}>
        <i aria-hidden="true" />
        {profile.enabled ? (draft ? 'Drafting replies' : 'Answering') : 'Switched off'}
      </span>
      <label className="toggle an-switch">
        <input
          type="checkbox"
          role="switch"
          aria-label="Assistant on"
          checked={profile.enabled}
          disabled={busy}
          onChange={(event) => void flip(event.target.checked)}
        />
        <span className="toggle-track" aria-hidden="true" />
      </label>
      {error ? <span className="bot-switch-error">{error}</span> : null}
    </div>
  );
}

function AddChatDialog({
  profile,
  onProfile,
  onCreated,
  onCancel,
}: {
  profile: BotProfile;
  onProfile: (fresh: BotProfile) => void;
  onCreated: (fresh: Inbox) => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="dialog-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onCancel()}>
      <div className="dialog bot-addchat-dialog" role="dialog" aria-modal="true" aria-label="Add a website chat">
        <h2>Add a website chat</h2>
        <p className="manage-card-sub">
          One per site you want kept apart — each has its own look, its own snippet and its own allowed sites.
        </p>
        <CreateChatForm profile={profile} onProfile={onProfile} onCreated={onCreated} onCancel={onCancel} />
      </div>
    </div>
  );
}
